import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Reaction } from '../../types';
import { AnimatedReactionsContainer } from './view';
import { AnimatedReactionItem } from './AnimatedReactionItem';

interface AnimatedReactionsProps {
    reactions: Reaction[];
    localParticipantId?: string;
}

export const AnimatedReactions: React.FC<AnimatedReactionsProps> = observer(({ reactions, localParticipantId }) => {
    const { t } = useTranslation();
    const offsetsRef = useRef<Map<string, number>>(new Map());
    const delaysRef = useRef<Map<string, number>>(new Map());

    const getOffsetX = useCallback((reactionId: string): number => {
        if (!offsetsRef.current.has(reactionId)) {
            const vw = typeof window !== 'undefined' ? window.innerWidth : 1000;
            const isSmallScreen = vw <= 768;
            const widthPercent = isSmallScreen ? 0.8 : 0.5;
            const maxWidth = isSmallScreen ? vw * 0.8 : 600;
            const containerWidth = Math.min(maxWidth, Math.max(200, vw * widthPercent));
            const padding = isSmallScreen ? 72 : 104;
            const usableWidth = Math.max(0, containerWidth - padding);
            const offset = Math.floor(Math.random() * usableWidth);
            offsetsRef.current.set(reactionId, offset);
        }
        return offsetsRef.current.get(reactionId)!;
    }, []);

    const getDelay = useCallback((reactionId: string): number => {
        if (!delaysRef.current.has(reactionId)) {
            const delay = Math.random() * 200;
            delaysRef.current.set(reactionId, delay);
        }
        return delaysRef.current.get(reactionId)!;
    }, []);

    useEffect(() => {
        const currentIds = new Set(reactions.map((r) => r.id));
        offsetsRef.current.forEach((_, id) => {
            if (!currentIds.has(id)) {
                offsetsRef.current.delete(id);
                delaysRef.current.delete(id);
            }
        });
    }, [reactions]);

    return (
        <AnimatedReactionsContainer>
            {reactions.map((reaction) => {
                const offsetX = getOffsetX(reaction.id);
                const delay = getDelay(reaction.id);

                const effectiveParticipantId = reaction.colorIdentity ?? reaction.participantId;

                const isLocal = localParticipantId && reaction.participantId === localParticipantId;
                const displayName = isLocal ? t('web_call_you_started') : reaction.participantName;

                return (
                    <AnimatedReactionItem
                        key={reaction.id}
                        reaction={reaction}
                        offsetX={offsetX}
                        delay={delay}
                        displayName={displayName}
                        effectiveParticipantId={effectiveParticipantId}
                        isFading={reaction.isFading}
                    />
                );
            })}
        </AnimatedReactionsContainer>
    );
});
